/**
 * SOURCE TYPES AND RETOUR METHODS — the two closed vocabularies both halves of the app need.
 *
 * ⚠️ THEY LIVE IN `packages/shared` FOR A REASON THAT IS NOT TIDINESS. The web screen needs
 * these as RUNTIME values (they populate a picker), and the API needs them to validate.
 * Importing them from `apps/api/src/catalog/sources.ts` would work in TypeScript and then
 * pull that module's graph — `pg`, `db/capabilities.js`, the whole server tree — into the
 * browser bundle, because a runtime import is not a type import.
 *
 * That trap has been paid for once already in this project: `agreements.ts` carries a note
 * saying the web imports it with `import type` ONLY, precisely so its embedded logo never
 * crosses into the bundle. A shared package is the version of that rule that cannot be got
 * wrong by accident.
 *
 * `packages/shared` SHIPS BUILT JAVASCRIPT (`dist`) — run `npm run build:shared` before
 * anything that consumes it.
 */
/**
 * What a source can be — six values, maintainer ruling 7 Aug 2026
 * (`docs/retour-management.md` §9-D).
 *
 * ⚠️ `lender` AND `supplier` ARE KEPT because rows have carried them since migration 001.
 * Widening a vocabulary is additive and safe; changing or removing a value existing data
 * uses is neither.
 *
 * ⚠️ ONLY `online` DRIVES DIFFERENT BEHAVIOUR — its retour needs a platform ticket and a
 * return label. The other five exist to be FILTERED and GROUPED by, which is what the
 * atelier asked for: *"for everything that is not online it's also very handy to obviously
 * filter on source type"*.
 *
 * ⚠️ NOT ENFORCED BY A DATABASE CHECK, deliberately — see migration 043's header. `kind` is
 * bare `text`; this set is the guarantee, and the cost is written down there.
 *
 * ORDERED, and the order is the picker's: the two that exist in data, then the one that
 * behaves differently, then the three that are purely descriptive.
 */
export const SOURCE_KIND_VALUES = [
    'lender',
    'supplier',
    'online',
    'designer',
    'showroom',
    'client_owned',
];
/**
 * How a retour travels.
 *
 * ⚠️ UNLIKE `kind`, THIS **IS** CONSTRAINED IN THE DATABASE (043). The asymmetry is the
 * point: a closed set the app generates from a picker gets a CHECK, because a value outside
 * it can only be a bug. An open vocabulary a human extends does not, because each extension
 * would mean a constraint rewrite against a live table.
 *
 * ⚠️ `posted` IS THE POST OFFICE, AND IT IS A METHOD, NOT A DESTINATION. The brief called it
 * a "retour location"; the parcel still goes to the vendor's warehouse. Storing it as a
 * location works exactly until a showroom also accepts post — see 043's header.
 */
export const RETOUR_METHODS = ['posted', 'hand', 'courier', 'collected'];
/** How each method reads on screen. The stored value is the key; this is never persisted. */
export const RETOUR_METHOD_LABELS = {
    posted: 'Posted',
    hand: 'By hand',
    courier: 'Courier',
    collected: 'Collected by them',
};
/** How each source type reads on screen. `client_owned` is the only one that needs it. */
export const SOURCE_KIND_LABELS = {
    lender: 'Lender',
    supplier: 'Supplier',
    online: 'Online order',
    designer: 'Designer',
    showroom: 'Showroom',
    client_owned: 'Client-owned',
};
/**
 * How a source with NO type yet reads — the null case of `SOURCE_KIND_LABELS`, one string for the
 * boxes filter sheet, the items filter sheet and the retour board's Source-type column. Hoisted
 * here at the third copy (0.1.460) rather than into `box-filters.ts`, which every screen already
 * reaches through `@mononoke/shared`: a new import edge from the retour screen into the web
 * filter modules split `box-filters` into its own preloaded chunk and put the boot path over
 * its 500 kB ceiling by eight bytes.
 */
export const NO_KIND_LABEL = 'No type yet';
//# sourceMappingURL=retour.js.map